/**
 * Shared shell for a saved-feedback summary card (incorrect logic / evidence).
 *
 * Renders the pale-yellow card with a bold title, an "Edit" affordance, a
 * leading dashed divider, and the caller's summary content beneath it.
 * @module FeedbackSummaryCard
 */
import React from "react";

/** Props for {@link FeedbackSummaryCard}. */
interface FeedbackSummaryCardProps {
  /** Card heading (e.g. "Incorrect Logic"). */
  title: string;
  /** Invoked when the reviewer clicks "Edit" to reopen the feedback form. */
  onEdit: () => void;
  /** The summary content shown below the divider. */
  children: React.ReactNode;
}

/**
 * Renders the saved-feedback summary card shell.
 *
 * @param props - Title, edit handler, and the summary content
 * @returns The rendered card
 */
export const FeedbackSummaryCard: React.FC<FeedbackSummaryCardProps> = ({
  title,
  onEdit,
  children,
}) => (
  <div className="flex flex-col items-start justify-center gap-3 self-stretch rounded-md bg-[#FFFDE6] p-[0.875rem]">
    <div className="flex items-center justify-between self-stretch">
      <span className="text-[0.75rem] font-bold leading-[1.125rem] text-[#0F0F0F]">
        {title}
      </span>
      <button
        type="button"
        onClick={onEdit}
        className="text-[0.75rem] font-semibold leading-[1rem] text-[#0056D6] underline"
      >
        Edit
      </button>
    </div>
    <div className="flex flex-col items-start gap-4 self-stretch">
      <div className="h-[0.0625rem] self-stretch border-t border-dashed border-[rgba(102,93,0,0.20)]" />
      {children}
    </div>
  </div>
);
